import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { formatTimestampFull, Permissions, useApiClient, useToastService } from '@@common';
import { ConfirmDialog } from '@@components';
import { AppCheckpointModel, UserInterfaceClient } from '../../../api/api';
import { useCurrentUserPermissions } from '../../../components/CurrentUserPermissionsContext';
import { ArtifactPanel } from '../../../common/components/ArtifactPanel';
import DialogHeaderActions from '../../../components/DialogHeaderActions';
import { usePersistedSetting } from '../../../common/hooks/usePersistedSetting';

type Props = {
    definitionId: string;
    changeSetId: string;
    hasUnsavedChanges: boolean;
    agentBusy: boolean;
    onRestored: () => Promise<void>;
};

function errorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'detail' in error && typeof error.detail === 'string') {
        return error.detail;
    }
    return error instanceof Error ? error.message : 'The request could not be completed. Refresh the comparison and try again.';
}

export default function AppCheckpointActions({ definitionId, changeSetId, hasUnsavedChanges, agentBusy, onRestored }: Props) {
    const client = useApiClient(UserInterfaceClient);
    const queryClient = useQueryClient();
    const toast = useToastService();
    const { hasPermission } = useCurrentUserPermissions();
    const [open, setOpen] = useState(false);
    const [isMaximized, setIsMaximized] = usePersistedSetting('appCheckpoint.dialog.maximized', false);
    const [selectedId, setSelectedId] = useState<string>();
    const [path, setPath] = useState('');
    const [confirmation, setConfirmation] = useState<AppCheckpointModel>();
    const [pending, setPending] = useState(false);
    const pendingRef = useRef(false);
    const [failure, setFailure] = useState<string>();
    const list = useQuery({
        queryKey: ['userinterface', 'checkpoints', definitionId, changeSetId],
        queryFn: ({ signal }) => client.getAppCheckpoints(definitionId, changeSetId, signal),
        staleTime: 15_000,
        refetchOnWindowFocus: false,
    });
    const checkpointId = selectedId ?? list.data?.[0]?.id;
    const comparison = useQuery({
        queryKey: ['userinterface', 'checkpoint-comparison', definitionId, changeSetId, checkpointId],
        queryFn: ({ signal }) => client.compareAppCheckpoint(definitionId, checkpointId!, changeSetId, signal),
        enabled: open && !!checkpointId,
        refetchOnWindowFocus: false,
    });
    const selectedPath = comparison.data?.changes?.some(change => change.path === path) ? path : '';
    const content = useQuery({
        queryKey: ['userinterface', 'checkpoint-content', definitionId, changeSetId, checkpointId, comparison.data?.checkpoint?.revision, selectedPath],
        queryFn: ({ signal }) => client.getAppCheckpointContent(definitionId, checkpointId!, changeSetId, selectedPath, signal),
        enabled: open && !!checkpointId && !!selectedPath,
        refetchOnWindowFocus: false,
    });
    const checkpoint = comparison.data?.checkpoint;
    const blocked = comparison.isError ? 'Refresh the comparison before undoing.'
        : hasUnsavedChanges ? 'Save or discard your unsaved App and source edits before undoing.'
        : agentBusy ? 'Wait for the agent to finish before undoing.'
        : !hasPermission(Permissions.AppUpdate) ? 'You do not have permission to update this App.'
        : !checkpoint?.canUndo ? checkpoint?.conflict || 'Only the latest verified change can be undone. Newer edits and uncertain changes are protected.'
        : undefined;
    const undoDisabledReason = pending ? 'Undo is in progress.'
        : comparison.isFetching ? 'Checking whether this change can be undone…'
        : !checkpoint ? 'Select an agent change and wait for the comparison to load.'
        : blocked;

    const refresh = async () => {
        await Promise.all([list.refetch(), comparison.refetch()]);
    };
    const undo = async () => {
        if (!confirmation || blocked || pendingRef.current || checkpointId !== confirmation.id) {
            return;
        }
        pendingRef.current = true;
        setPending(true);
        setFailure(undefined);
        let restored = false;
        try {
            await client.undoLastAppCheckpoint(definitionId, changeSetId, {
                checkpointId: confirmation.id,
                expectedRevision: confirmation.revision,
            });
            restored = true;
            setConfirmation(undefined);
            await queryClient.invalidateQueries({ queryKey: ['userinterface', 'checkpoints', definitionId] });
            await comparison.refetch();
            await onRestored();
            toast.success('Last agent change undone. App and React source restored.');
        } catch (error) {
            setConfirmation(undefined);
            setFailure(restored ? 'Undo succeeded, but the designer could not reload. Refresh the designer to see the restored state.' : errorMessage(error));
            if (!restored) {
                await refresh();
            }
        } finally {
            pendingRef.current = false;
            setPending(false);
        }
    };

    return <>
        <Button size="small" startIcon={<HistoryIcon />} onClick={() => {
            setOpen(true);
            setFailure(undefined);
            void list.refetch();
        }}>View changes</Button>
        <Dialog open={open} aria-labelledby="app-checkpoint-dialog-title" maxWidth="xl" fullWidth fullScreen={isMaximized}
            PaperProps={{ sx: { height: isMaximized ? '100%' : '85vh' } }} onClose={() => { if (!pending) setOpen(false); }}>
            <DialogTitle id="app-checkpoint-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                Agent changes
                <DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized(value => !value)}
                    onClose={() => { if (!pending) setOpen(false); }} closeDisabled={pending} />
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Stack spacing={2} sx={{ flexShrink: 0 }}>
                    <Typography variant="body2">Compare saved App and React source changes. Undo creates a restored App version and retains history; it does not deploy the App or undo external data.</Typography>
                    {failure && <Alert severity="error">{failure}</Alert>}
                    {list.isError && <Alert severity="error">{errorMessage(list.error)}</Alert>}
                    {list.isPending && <Typography>Loading changes…</Typography>}
                    {list.data?.length === 0 && <Alert severity="info">No agent checkpoints have been recorded for this App and change set yet.</Alert>}
                    {!!list.data?.length && <TextField select label="Agent change" value={checkpointId ?? ''} disabled={pending} onChange={event => {
                        setSelectedId(event.target.value);
                        setPath('');
                        setConfirmation(undefined);
                    }}>
                        {list.data.map(item => <MenuItem key={item.id} value={item.id}>
                            {item.createdDate ? formatTimestampFull(item.createdDate) : item.assignmentId} — {item.state}{item.canUndo ? ' (Undo available)' : ''}
                        </MenuItem>)}
                    </TextField>}
                    {comparison.isFetching && <Typography>Checking saved changes…</Typography>}
                    {comparison.isError && <Alert severity="error">{errorMessage(comparison.error)}</Alert>}
                    {checkpoint && blocked && <Alert severity="info">{blocked}</Alert>}
                    {comparison.data?.changes?.length === 0 && <Typography>No saved content differences.</Typography>}
                </Stack>
                {!!comparison.data?.changes?.length && <Box sx={{
                    display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, gap: 2, mt: 2,
                }}>
                    <Box component="nav" aria-label="Changed artifacts" sx={{
                        width: { xs: '100%', md: 300 }, height: { xs: 200, md: '100%' }, flexShrink: 0, minHeight: 0,
                    }}>
                        <ArtifactPanel title="Artifacts" width="100%" categories={[{
                            id: 'changed-artifacts', label: 'Changed items', icon: <FolderOutlinedIcon fontSize="small" />,
                            items: comparison.data.changes.map(change => ({
                                id: change.path, label: change.path, icon: <InsertDriveFileOutlinedIcon fontSize="small" />,
                                subtitle: `${change.kind} (${change.beforeLength} → ${change.afterLength} characters)`,
                            })),
                        }]} selectedItemId={selectedPath || null} onSelectItem={itemId => setPath(itemId ?? '')}
                            searchPlaceholder="Search changed artifacts…" />
                    </Box>
                    <Box component="section" aria-label="Artifact comparison" sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
                        {!selectedPath && <Typography color="text.secondary" sx={{ p: 2 }}>Select an artifact to compare.</Typography>}
                        {selectedPath && <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>{selectedPath}</Typography>}
                        {content.isFetching && <Typography>Loading comparison content…</Typography>}
                        {content.isError && <Alert severity="error">{errorMessage(content.error)}</Alert>}
                        {selectedPath && content.data && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, flex: 1, minHeight: 0 }}>
                            {(['before', 'after'] as const).map(side => <Box key={side} sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" sx={{ pb: 1 }}>{side === 'before' ? 'Before' : 'After'}</Typography>
                                <Box component="pre" sx={{ flex: 1, m: 0, overflow: 'auto', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                    {content.data[side] ?? '(Not present)'}
                                </Box>
                            </Box>)}
                        </Box>}
                    </Box>
                </Box>}
            </DialogContent>
            <DialogActions>
                <Tooltip title={undoDisabledReason ?? 'Restore the entire agent change shown above.'}>
                    <span><Button disabled={!!undoDisabledReason} onClick={() => setConfirmation(checkpoint)}>Undo last change</Button></span>
                </Tooltip>
                <Button disabled={pending} onClick={() => { void refresh(); }}>Refresh comparison</Button>
                <Button disabled={pending} onClick={() => setOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>
        <ConfirmDialog open={!!confirmation} title="Undo last agent change?"
            message="Restore the saved App definition, React source and published build reference from before this assignment? Newer edits will never be overwritten."
            confirmText={pending ? 'Undoing…' : 'Undo change'} confirmDisabled={pending || !!blocked} cancelDisabled={pending}
            onConfirm={() => { void undo(); }} onCancel={() => { if (!pendingRef.current) setConfirmation(undefined); }} />
    </>;
}
